/**
 * shadcn/Tailwind replacement for EntityDetailContentBodyFullScreen.
 * Full-screen dialog for viewing Content entity Body (markdown or HTML).
 */

import React from 'react';
import { X, Loader2, Link2 } from 'lucide-react';
import { ShadcnContentViewer } from '../../common/ShadcnContentViewer';
import { HtmlPreview } from '../../common/HtmlPreview';
import { isContent } from '../../../utils/entityTypeDetection';
import { NON_INDEXED_PROPERTY_STUB } from '../../../utils/entityPropertyUtils';
import { detectFieldType } from '../../../utils/fieldTypeDetection';
import {
    CONTENT_BODY_HASH_PARAM,
    CONTENT_BODY_LOADED,
    clearContentBodyHashParam,
    setHashQueryParam,
} from '../../../utils/hashQueryParams';
import { getContentBodyValueAndKey } from './entityDetailValueUtils';

export interface ShadcnContentBodyFullScreenProps {
    opened: boolean;
    onClose: () => void;
    fullEntity: any;
    entity: any;
    entityType: string;
    contentBodyMode: string | null;
    copyDeepLinkWithBodyLoaded: () => void;
}

const renderBody = (
    fullEntity: any,
    entity: any,
    entityType: string,
    contentBodyMode: string | null,
    copyDeepLinkWithBodyLoaded: () => void,
): React.ReactNode => {
    const ent = fullEntity ?? entity;
    const { value: v } = getContentBodyValueAndKey(ent);
    const str = typeof v === 'string' ? v : '';
    const stub = v === NON_INDEXED_PROPERTY_STUB || v === undefined;

    if (stub) {
        return (
            <div className="flex items-center gap-2 flex-wrap">
                <Loader2 size={16} className="animate-spin text-muted-foreground" />
                <span className="text-sm text-muted-foreground">Loading Body...</span>
                {isContent(entityType) && contentBodyMode == null && (
                    <button
                        className="text-sm text-primary underline underline-offset-2 hover:text-primary/80 bg-transparent border-none cursor-pointer"
                        onClick={() => setHashQueryParam(CONTENT_BODY_HASH_PARAM, CONTENT_BODY_LOADED)}
                    >
                        Reload with Body included
                    </button>
                )}
            </div>
        );
    }

    const info = detectFieldType('Body', str, entityType, fullEntity, 'basic');
    if (info.isContentBodyMarkdown) {
        return (
            <div className="flex flex-col h-[calc(100vh-220px)]">
                <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
                    <span className="text-sm text-muted-foreground">Body</span>
                    {isContent(entityType) && (
                        <div className="flex items-center gap-3">
                            {contentBodyMode !== CONTENT_BODY_LOADED && (
                                <button
                                    className="text-sm text-primary underline underline-offset-2 hover:text-primary/80 bg-transparent border-none cursor-pointer"
                                    onClick={() => setHashQueryParam(CONTENT_BODY_HASH_PARAM, CONTENT_BODY_LOADED)}
                                >
                                    Include Body in link
                                </button>
                            )}
                            <button
                                className="inline-flex items-center gap-1 text-sm text-primary underline underline-offset-2 hover:text-primary/80 bg-transparent border-none cursor-pointer"
                                onClick={copyDeepLinkWithBodyLoaded}
                            >
                                <Link2 size={12} />
                                Copy link (includes Body)
                            </button>
                        </div>
                    )}
                </div>
                <ShadcnContentViewer
                    content={str}
                    emptyMessage="No content"
                    height="calc(100vh - 300px)"
                />
            </div>
        );
    }

    return <HtmlPreview content={str} emptyMessage="No content" height="calc(100vh - 220px)" />;
};

export const ShadcnContentBodyFullScreen: React.FC<ShadcnContentBodyFullScreenProps> = ({
    opened,
    onClose,
    fullEntity,
    entity,
    entityType,
    contentBodyMode,
    copyDeepLinkWithBodyLoaded,
}) => {
    if (!opened) return null;

    const handleClose = () => {
        onClose();
        clearContentBodyHashParam();
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
            {/* Backdrop */}
            <div className="absolute inset-0 bg-background/80 backdrop-blur-sm" onClick={handleClose} />

            {/* Dialog */}
            <div className="relative w-[100vw] max-w-[100vw] h-[95vh] max-h-[95vh] bg-card border border-border rounded-lg shadow-lg flex flex-col mx-4">
                {/* Header */}
                <div className="flex items-center justify-between px-4 py-3 border-b border-border">
                    <h2 className="text-base font-semibold text-foreground">Body</h2>
                    <button
                        onClick={handleClose}
                        className="inline-flex items-center justify-center rounded-md p-1.5 text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
                    >
                        <X size={18} />
                    </button>
                </div>

                {/* Body */}
                <div className="flex-1 overflow-auto p-4">
                    {renderBody(fullEntity, entity, entityType, contentBodyMode, copyDeepLinkWithBodyLoaded)}
                </div>
            </div>
        </div>
    );
};
